import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';

/**
 * Global language / localisation context.
 *
 *  - language: current language code ('en' | 'es' | 'pt' | 'ru' | 'zh' | 'ja' | 'fr' | 'hi' | 'ar')
 *  - setLanguage(code): persists to localStorage + backend, reloads UI catalog
 *  - t(key, englishFallback): translate a UI string key
 *  - translateContent(texts[]): batch-translate dynamic content → {original: translated} (cached)
 *  - rtl / ready flags
 *
 * English is a no-op (returns source text), so the app behaves exactly as
 * before until a non-English language is chosen.
 */
const LanguageContext = createContext(null);

export const useLanguage = () => useContext(LanguageContext) || {
  language: 'en', setLanguage: () => {}, t: (_k, f) => f, translateContent: async (x) => Object.fromEntries(x.map(t => [t, t])), rtl: false, ready: true, translating: false, setTranslating: () => {},
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export function LanguageProvider({ children, user }) {
  const [language, setLanguageState] = useState(
    () => localStorage.getItem('lang') || user?.preferred_language || 'en'
  );
  const [dict, setDict] = useState({});
  const [rtl, setRtl] = useState(false);
  const [ready, setReady] = useState(language === 'en');
  const [translating, setTranslating] = useState(false);
  const contentCache = useRef({}); // `${lang}:${text}` -> translated

  // Load the UI catalog whenever the language changes
  useEffect(() => {
    let active = true;
    if (language === 'en') {
      setDict({});
      setRtl(false);
      setReady(true);
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'en');
      return;
    }
    setReady(false);
    axios.get(`${API}/i18n/ui/${language}`, { headers: authHeaders() })
      .then((r) => {
        if (!active) return;
        setDict(r.data.strings || {});
        setRtl(!!r.data.rtl);
        document.documentElement.setAttribute('dir', r.data.rtl ? 'rtl' : 'ltr');
        document.documentElement.setAttribute('lang', language);
      })
      .catch(() => {})
      .finally(() => { if (active) setReady(true); });
    return () => { active = false; };
  }, [language]);

  const setLanguage = useCallback(async (lang) => {
    setLanguageState(lang);
    localStorage.setItem('lang', lang);
    try {
      await axios.post(`${API}/i18n/preferred-language`, { language: lang }, { headers: authHeaders() });
    } catch { /* preference persists locally regardless */ }
  }, []);

  // First-login browser-language auto-detect (one-time, authenticated users only)
  const autoDetectedRef = useRef(false);
  useEffect(() => {
    if (autoDetectedRef.current) return;
    if (!localStorage.getItem('token')) return; // only for logged-in users
    autoDetectedRef.current = true;

    // Respect an existing explicit choice (this device or saved on the account)
    if (localStorage.getItem('lang')) return;
    if (user?.preferred_language && user.preferred_language !== 'en') {
      setLanguageState(user.preferred_language);
      localStorage.setItem('lang', user.preferred_language);
      return;
    }

    const supported = ['es', 'pt', 'ru', 'zh', 'ja', 'fr', 'hi', 'ar'];
    const detected = (navigator.language || 'en').slice(0, 2).toLowerCase();
    if (supported.includes(detected)) {
      setLanguage(detected);
      import('sonner').then(({ toast }) => {
        toast('We switched to your browser language', {
          description: 'You can change it anytime from the language menu.',
          action: { label: 'Switch to English', onClick: () => setLanguage('en') },
          duration: 9000,
        });
      });
    }
  }, [user, setLanguage]);

  const t = useCallback((key, fallback = '') => {
    if (language === 'en') return fallback || key;
    return dict[key] || fallback || key;
  }, [language, dict]);

  const translateContent = useCallback(async (texts) => {
    const list = (texts || []).filter((x) => x && x.trim());
    if (language === 'en' || list.length === 0) {
      return Object.fromEntries((texts || []).map((t) => [t, t]));
    }
    const missing = list.filter((t) => contentCache.current[`${language}:${t}`] === undefined);
    if (missing.length) {
      try {
        const r = await axios.post(
          `${API}/i18n/translate`,
          { texts: missing, target_language: language },
          { headers: authHeaders() }
        );
        Object.entries(r.data.translations || {}).forEach(([o, tr]) => {
          contentCache.current[`${language}:${o}`] = tr;
        });
      } catch { /* fall back to original below */ }
    }
    return Object.fromEntries(
      (texts || []).map((t) => [t, contentCache.current[`${language}:${t}`] ?? t])
    );
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translateContent, rtl, ready, translating, setTranslating }}>
      {children}
    </LanguageContext.Provider>
  );
}

/** Hook: returns `text` translated to the active language (auto-updates). */
export function useTranslatedText(text) {
  const { language, translateContent } = useLanguage();
  const [out, setOut] = useState(text);
  useEffect(() => {
    let active = true;
    if (!text || language === 'en') { setOut(text); return; }
    translateContent([text]).then((m) => { if (active) setOut(m[text] ?? text); });
    return () => { active = false; };
  }, [text, language, translateContent]);
  return out;
}

/**
 * Hook: batch-translate an array of strings once and return a getter `tr(text)`.
 * Efficient for list/detail pages — one request per render instead of one per item.
 */
export function useTranslatedMap(texts) {
  const { language, translateContent } = useLanguage();
  const [map, setMap] = useState({});
  const list = (texts || []).filter((x) => x && typeof x === 'string' && x.trim());
  const depKey = list.join('\u0001');
  useEffect(() => {
    let active = true;
    if (language === 'en' || list.length === 0) { setMap({}); return; }
    translateContent(list).then((m) => { if (active) setMap(m); });
    return () => { active = false; };
    // depKey + language fully capture the inputs
  }, [depKey, language]);
  return (t) => (t && map[t]) || t;
}

/** Renders `text` translated to the active language. */
export function TranslatableText({ text, as: Tag = 'span', ...rest }) {
  const out = useTranslatedText(text);
  return <Tag {...rest}>{out}</Tag>;
}
